import type { ReactNode } from 'react';

import { HeroMarkParts, LogoLockup } from '@/components/brand/art';
import { cn } from '@/lib/utils';

/**
 * The signed-out frame (direction B, `/prototype/b/signin`): the form column on the left with the
 * logo, the isometric hero mark on the dark brand surface on the right. The art column is
 * decorative and appears from 1024px up; below that the form takes the whole width.
 *
 * Used by /auth and /approval/login. The frame owns layout only: redirects stay in AuthLayout and
 * the pages.
 */
export function AuthFrame({
  children,
  topRight,
  footer,
  caption,
  className,
}: {
  children: ReactNode;
  /** Top-right of the form column, e.g. the language switch. */
  topRight?: ReactNode;
  /** Bottom of the form column (version line, portal name). */
  footer?: ReactNode;
  /** One line over the art. */
  caption?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid min-h-dvh bg-canvas font-ui text-fg lg:grid-cols-[minmax(440px,0.85fr)_minmax(0,1fr)]',
        className
      )}
    >
      <div className="grid grid-rows-[auto_1fr_auto] gap-8 bg-raised px-4 py-6 sm:px-8 lg:px-12 lg:py-7">
        <div className="flex items-center justify-between gap-4">
          <AuthLogo />
          {topRight}
        </div>
        <main className="flex w-full items-center justify-center">
          <div className="w-full max-w-[360px]">{children}</div>
        </main>
        {footer && <div className="text-center text-fg-muted text-xs leading-relaxed">{footer}</div>}
      </div>

      {/* Decorative: the mark and the caption repeat nothing the form needs. */}
      <aside
        aria-hidden="true"
        className="lm-dark relative hidden place-items-center overflow-hidden border-line border-l bg-canvas lg:grid"
      >
        <div className="absolute inset-0 bg-iso-grid [mask-image:radial-gradient(ellipse_at_50%_45%,#000_0%,transparent_72%)]" />
        <div className="absolute top-[44%] left-1/2 size-[520px] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(circle_at_40%_40%,color-mix(in_srgb,var(--lm-blue)_28%,transparent),transparent_60%),radial-gradient(circle_at_65%_60%,color-mix(in_srgb,var(--lm-violet)_24%,transparent),transparent_60%)] blur-[46px]" />
        <svg
          className="relative mb-20 h-auto w-[min(300px,40%)] drop-shadow-[0_0_14px_color-mix(in_srgb,var(--lm-blue)_45%,transparent)]"
          viewBox="395 300 270 310"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <HeroMarkParts />
        </svg>
        {caption && (
          <p className="absolute right-12 bottom-11 left-12 max-w-[32em] font-bold text-fg text-xl leading-snug tracking-[-0.01em]">
            {caption}
          </p>
        )}
      </aside>
    </div>
  );
}

/** The lockup in the mode's ink: white wordmark on dark, navy on light (DESIGN.md logo rules). */
function AuthLogo() {
  return (
    <span className="inline-block leading-none [&_path[fill='#C8D0DF']]:fill-(--lm-logo-ink-2) [&_path[fill='#FFFFFF']]:fill-(--lm-logo-ink) [&_svg]:block [&_svg]:h-9 [&_svg]:w-auto">
      <LogoLockup id="auth" />
    </span>
  );
}
